import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';
import './LandingPage.css';
import './GeneratorTimova.css';
import './RasporedTurnira.css';
import { useSeo } from '../hooks/useSeo';
import FaqItem from './FaqItem';
import PublicPageNav from './PublicPageNav';


const FORMAT_OPTIONS = [
  {
    id: 'round-robin',
    label: 'Svi protiv svih',
    hint: 'Round-robin (mini liga)',
  },
  {
    id: 'knockout',
    label: 'Kup / eliminacija',
    hint: 'Tko izgubi - ispada',
  },
];

const parseTeams = (text) =>
  text
    .split(/\r?\n|,|;/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// ── Round-robin (circle / Berger method) ──────────────────────────
const generateRoundRobin = (teams, doubleRound = false) => {
  const list = shuffle(teams);
  const BYE = '— pauza —';
  if (list.length % 2 === 1) list.push(BYE);

  const n = list.length;
  const half = n / 2;
  const rounds = [];
  let arr = [...list];

  for (let r = 0; r < n - 1; r++) {
    const matches = [];
    for (let i = 0; i < half; i++) {
      const home = arr[i];
      const away = arr[n - 1 - i];
      if (home !== BYE && away !== BYE) {
        // alterniraj domaćina/gosta radi ravnomjernog broja domaćih utakmica
        if (r % 2 === 1 && i !== 0) {
          matches.push({ home: away, away: home, bye: false });
        } else {
          matches.push({ home, away, bye: false });
        }
      } else {
        const benched = home === BYE ? away : home;
        matches.push({ home: benched, away: null, bye: true });
      }
    }
    rounds.push(matches);
    // rotiraj: prvi element ostaje fiksiran, ostali idu u krug
    arr = [arr[0], arr[n - 1], ...arr.slice(1, n - 1)];
  }

  if (doubleRound) {
    // drugi krug - obrnuto domaćinstvo
    const second = rounds.map((round) =>
      round.map((m) =>
        m.bye ? m : { home: m.away, away: m.home, bye: false }
      )
    );
    return [...rounds, ...second];
  }

  return rounds;
};

// ── Single elimination (kup) ──────────────────────────────────────
const generateKnockout = (teams) => {
  const shuffled = shuffle(teams);
  const n = shuffled.length;
  let size = 1;
  while (size < n) size *= 2;
  const byesNeeded = size - n;

  // Popuni pozicije tako da BYE ide protiv nositelja (gornji seedovi)
  const padded = [];
  for (let i = 0; i < size; i++) {
    if (i < n) padded.push({ name: shuffled[i], isBye: false });
    else padded.push({ name: null, isBye: true });
  }
  // Raspodijeli BYE-ove parno u bracketu (ubacuj ih iza najboljih seedova)
  const arranged = [];
  for (let i = 0; i < size / 2; i++) {
    const a = padded[i];
    const b = padded[size - 1 - i];
    arranged.push(a, b);
  }

  const rounds = [];
  const firstRound = [];
  for (let i = 0; i < arranged.length; i += 2) {
    const A = arranged[i];
    const B = arranged[i + 1];
    firstRound.push({
      home: A.isBye ? null : A.name,
      away: B.isBye ? null : B.name,
      bye: A.isBye || B.isBye,
      advance: A.isBye ? B.name : B.isBye ? A.name : null,
    });
  }
  rounds.push(firstRound);

  let prev = firstRound.length;
  while (prev > 1) {
    const matches = [];
    for (let i = 0; i < prev / 2; i++) {
      matches.push({ home: null, away: null, bye: false, tbd: true });
    }
    rounds.push(matches);
    prev = prev / 2;
  }

  return { rounds, totalTeams: n, bracketSize: size, byes: byesNeeded };
};

// ── Format vremena ────────────────────────────────────────────────
const minutesToTime = (startTime, offsetMin) => {
  if (!startTime) return null;
  const [h, m] = startTime.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  const total = h * 60 + m + offsetMin;
  const hh = Math.floor((total % (24 * 60)) / 60);
  const mm = total % 60;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const getRoundName = (index, totalRounds, format) => {
  if (format !== 'knockout') return `Kolo ${index + 1}`;
  const fromEnd = totalRounds - index;
  if (fromEnd === 1) return 'Finale';
  if (fromEnd === 2) return 'Polufinale';
  if (fromEnd === 3) return 'Četvrtfinale';
  if (fromEnd === 4) return 'Osmina finala';
  if (fromEnd === 5) return 'Šesnaestina finala';
  return `Runda ${index + 1}`;
};

const RasporedTurnira = () => {
  useSeo({
    title: 'Raspored turnira - generator rasporeda svi protiv svih i kup | StatArena',
    description:
      'Besplatni generator rasporeda turnira. Unesi ekipe i aplikacija napravi raspored svi protiv svih (round-robin) ili kup sustav s eliminacijom. Idealno za mali nogomet, futsal, mini ligu i firmaške turnire. Bez registracije.',
    canonical: 'https://statarena.app/raspored-turnira',
    robots: 'index, follow, max-image-preview:large',
    ogUrl: 'https://statarena.app/raspored-turnira',
    ogTitle: 'Generator rasporeda turnira - svi protiv svih i kup sustav',
    ogDescription:
      'Besplatni online alat za raspored nogometnog turnira. Round-robin ili kup, par klikova i raspored je gotov.',
  });

  const [teamsText, setTeamsText] = useState('');
  const [format, setFormat] = useState('round-robin');
  const [doubleRound, setDoubleRound] = useState(false);
  const [pitches, setPitches] = useState(1);
  const [matchDuration, setMatchDuration] = useState(15);
  const [breakDuration, setBreakDuration] = useState(5);
  const [startTime, setStartTime] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const resultsRef = useRef(null);

  const teams = useMemo(() => parseTeams(teamsText), [teamsText]);

  const stats = useMemo(() => {
    const t = teams.length;
    if (t < 2) return null;
    if (format === 'round-robin') {
      const roundsCount = t % 2 === 0 ? t - 1 : t;
      const matchesPerRound = Math.floor(t / 2);
      const totalMatches = (t * (t - 1)) / 2 * (doubleRound ? 2 : 1);
      const rounds = doubleRound ? roundsCount * 2 : roundsCount;
      return { totalMatches, rounds, matchesPerRound };
    }
    // knockout
    let size = 1;
    while (size < t) size *= 2;
    const totalMatches = t - 1;
    const rounds = Math.log2(size);
    return { totalMatches, rounds, matchesPerRound: null };
  }, [teams, format, doubleRound]);

  const handleGenerate = () => {
    setCopied(false);
    if (teams.length < 2) {
      setError('Unesi barem 2 ekipe (svaka u novi red).');
      setSchedule(null);
      return;
    }
    if (teams.length > 32) {
      setError('Maksimalno 32 ekipe. Za veće turnire javi nam se na podršku.');
      setSchedule(null);
      return;
    }
    setError('');

    if (format === 'round-robin') {
      const rounds = generateRoundRobin(teams, doubleRound);
      setSchedule({ format, rounds, teamsCount: teams.length });
    } else {
      const ko = generateKnockout(teams);
      setSchedule({
        format,
        rounds: ko.rounds,
        teamsCount: teams.length,
        bracketSize: ko.bracketSize,
        byes: ko.byes,
      });
    }

    setTimeout(() => {
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleReroll = () => {
    setCopied(false);
    handleGenerate();
  };

  const handleClear = () => {
    setTeamsText('');
    setSchedule(null);
    setError('');
    setCopied(false);
  };

  const handleExample = () => {
    setTeamsText(
      [
        'Dinamo',
        'Hajduk',
        'Rijeka',
        'Osijek',
        'Lokomotiva',
        'Gorica',
        'Varaždin',
        'Istra',
      ].join('\n')
    );
    setError('');
  };

  // ── Izračun vremena za svaku utakmicu ──────────────────────────
  const scheduleWithTimes = useMemo(() => {
    if (!schedule) return null;
    const rounds = schedule.rounds.map((round, rIdx) => {
      // broj utakmica u ovom kolu koje se stvarno igraju (bez bye-eva)
      const playable = round.filter((m) => !m.bye && !m.tbd);
      const slotsNeeded = Math.ceil(playable.length / pitches);
      return { round, rIdx, slotsNeeded };
    });

    // kumulativni offset po kolu
    let cumulativeMin = 0;
    const roundOffsets = rounds.map(({ slotsNeeded }) => {
      const offset = cumulativeMin;
      cumulativeMin += slotsNeeded * matchDuration + (slotsNeeded > 0 ? breakDuration : 0);
      return offset;
    });

    return { rounds: schedule.rounds, roundOffsets };
  }, [schedule, pitches, matchDuration, breakDuration]);

  const getMatchTime = (roundIdx, indexInRound) => {
    if (!startTime || !scheduleWithTimes) return null;
    const roundOffset = scheduleWithTimes.roundOffsets[roundIdx];
    const slotInRound = Math.floor(indexInRound / pitches);
    return minutesToTime(startTime, roundOffset + slotInRound * matchDuration);
  };

  const handleCopy = async () => {
    if (!schedule) return;
    const lines = [];
    lines.push(
      format === 'round-robin'
        ? `RASPORED TURNIRA - svi protiv svih${doubleRound ? ' (dvokružni)' : ''}`
        : 'RASPORED TURNIRA - kup sustav'
    );
    lines.push(`${teams.length} ekipa${startTime ? ` · početak ${startTime}` : ''}`);
    lines.push('');
    schedule.rounds.forEach((round, rIdx) => {
      lines.push(`── ${getRoundName(rIdx, schedule.rounds.length, format)} ──`);
      round.forEach((m, mIdx) => {
        const time = getMatchTime(rIdx, mIdx);
        const prefix = time ? `${time}  ` : '';
        if (m.tbd) {
          lines.push(`${prefix}pobjednik vs pobjednik`);
        } else if (m.bye) {
          if (m.advance) {
            lines.push(`${prefix}${m.advance} - prolaz (slobodan)`);
          } else {
            lines.push(`${prefix}${m.home} - pauza`);
          }
        } else {
          lines.push(`${prefix}${m.home} - ${m.away}`);
        }
      });
      lines.push('');
    });
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Kopiranje nije uspjelo:', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="landing">
      <PublicPageNav />

      <section className="landing-hero gt-hero">
        <span className="hero-eyebrow">Besplatno · Bez registracije · Radi na mobitelu</span>
        <h1 className="landing-hero-title">
          Raspored turnira - <span className="accent">svi protiv svih ili kup</span> u par klikova
        </h1>
        <p className="landing-hero-subtitle">
          Unesi ekipe, odaberi format turnira i aplikacija će automatski napraviti
          raspored utakmica - round-robin (svi protiv svih) ili kup sustav s
          eliminacijom. Idealno za mali nogomet, futsal, firmaške turnire, školska
          natjecanja i mini ligu među prijateljima. Bez Excela, bez ždrijeba iz
          šešira, bez rasprave tko igra s kim.
        </p>
      </section>

      <section className="gt-tool-section">
        <div className="gt-tool">
          <div className="gt-tool-grid">
            <div className="gt-input-col">
              <label htmlFor="rt-teams" className="gt-label">
                Ekipe / timovi <span className="gt-label-hint">(svaka u novi red ili odvojeno zarezom)</span>
              </label>
              <textarea
                id="rt-teams"
                className="gt-textarea"
                placeholder={'Dinamo\nHajduk\nRijeka\nOsijek\n...'}
                value={teamsText}
                onChange={(e) => setTeamsText(e.target.value)}
                rows={10}
              />
              <div className="gt-counter">
                <span>{teams.length} {teams.length === 1 ? 'ekipa' : 'ekipa'}</span>
                <button type="button" className="gt-link-btn" onClick={handleExample}>
                  Ubaci primjer
                </button>
                {teamsText && (
                  <button type="button" className="gt-link-btn" onClick={handleClear}>
                    Obriši sve
                  </button>
                )}
              </div>
            </div>

            <div className="gt-controls-col">
              <label className="gt-label">Format turnira</label>
              <div className="rt-format-picker">
                {FORMAT_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`rt-format-btn ${format === opt.id ? 'active' : ''}`}
                    onClick={() => setFormat(opt.id)}
                  >
                    <strong>{opt.label}</strong>
                    <span>{opt.hint}</span>
                  </button>
                ))}
              </div>

              {format === 'round-robin' && (
                <label className="rt-checkbox">
                  <input
                    type="checkbox"
                    checked={doubleRound}
                    onChange={(e) => setDoubleRound(e.target.checked)}
                  />
                  <span>Dvokružno (svatko sa svakim dvaput)</span>
                </label>
              )}

              <details className="rt-advanced">
                <summary>Napredne postavke (termin i trajanje)</summary>
                <div className="rt-advanced-grid">
                  <label className="rt-field">
                    <span>Početak</span>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </label>
                  <label className="rt-field">
                    <span>Trajanje (min)</span>
                    <input
                      type="number"
                      min="1"
                      max="180"
                      value={matchDuration}
                      onChange={(e) => setMatchDuration(Number(e.target.value) || 15)}
                    />
                  </label>
                  <label className="rt-field">
                    <span>Pauza (min)</span>
                    <input
                      type="number"
                      min="0"
                      max="60"
                      value={breakDuration}
                      onChange={(e) => setBreakDuration(Number(e.target.value) || 0)}
                    />
                  </label>
                  <label className="rt-field">
                    <span>Broj terena</span>
                    <input
                      type="number"
                      min="1"
                      max="8"
                      value={pitches}
                      onChange={(e) => setPitches(Number(e.target.value) || 1)}
                    />
                  </label>
                </div>
              </details>

              {stats && (
                <div className="gt-summary">
                  <div className="gt-summary-row">
                    <span>Ukupno utakmica:</span>
                    <strong>{stats.totalMatches}</strong>
                  </div>
                  <div className="gt-summary-row">
                    <span>{format === 'knockout' ? 'Rundi' : 'Kola'}:</span>
                    <strong>{stats.rounds}</strong>
                  </div>
                  {stats.matchesPerRound && (
                    <div className="gt-summary-row">
                      <span>Po kolu:</span>
                      <strong>{stats.matchesPerRound} ut.</strong>
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                className="btn btn-primary btn-lg gt-generate-btn"
                onClick={handleGenerate}
              >
                Napravi raspored
              </button>

              {error && <div className="gt-error">{error}</div>}
            </div>
          </div>
        </div>

        {schedule && (
          <div className="gt-results rt-results" ref={resultsRef}>
            <div className="gt-results-header">
              <h2>
                {format === 'round-robin' ? 'Raspored utakmica' : 'Turnirska ljestva (kup)'}
              </h2>
              <div className="gt-results-actions">
                <button type="button" className="btn btn-secondary" onClick={handleReroll}>
                  <span role="img" aria-label="Ponovi">🔄</span> Novi ždrijeb
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCopy}>
                  <span role="img" aria-label="Kopiraj">📋</span> {copied ? 'Kopirano!' : 'Kopiraj'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handlePrint}>
                  <span role="img" aria-label="Print">🖨️</span> Isprintaj
                </button>
              </div>
            </div>

            <div className="rt-rounds">
              {schedule.rounds.map((round, rIdx) => (
                <div key={rIdx} className="rt-round-card">
                  <div className="rt-round-header">
                    <h3>{getRoundName(rIdx, schedule.rounds.length, format)}</h3>
                    <span className="rt-round-count">
                      {round.filter((m) => !m.tbd && !m.bye).length} ut.
                    </span>
                  </div>
                  <ul className="rt-match-list">
                    {round.map((m, mIdx) => {
                      const time = getMatchTime(rIdx, mIdx);
                      if (m.tbd) {
                        return (
                          <li key={mIdx} className="rt-match rt-match-tbd">
                            {time && <span className="rt-match-time">{time}</span>}
                            <span className="rt-team">pobjednik</span>
                            <span className="rt-vs">vs</span>
                            <span className="rt-team">pobjednik</span>
                          </li>
                        );
                      }
                      if (m.bye) {
                        return (
                          <li key={mIdx} className="rt-match rt-match-bye">
                            {time && <span className="rt-match-time" />}
                            <span className="rt-team">{m.advance || m.home}</span>
                            <span className="rt-vs rt-vs-bye">
                              {m.advance ? 'prolaz' : 'pauza'}
                            </span>
                            <span className="rt-team rt-team-bye">—</span>
                          </li>
                        );
                      }
                      return (
                        <li key={mIdx} className="rt-match">
                          {time && <span className="rt-match-time">{time}</span>}
                          <span className="rt-team">{m.home}</span>
                          <span className="rt-vs">vs</span>
                          <span className="rt-team">{m.away}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="landing-section">
        <div className="section-header landing-section-header">
          <h2>Za koje sve turnire možeš koristiti generator rasporeda</h2>
          <p className="landing-section-sub">
            Alat radi za bilo koji sport ili aktivnost u kojoj se više ekipa
            mora rasporediti po utakmicama - ne samo nogomet.
          </p>
        </div>

        <div className="landing-features">
          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Nogomet">⚽</span></div>
            <h3>Mali nogomet i futsal turnir</h3>
            <p>
              Vikend turnir 5x5 ili 6x6, firmaški kup, turnir u dvorani -
              upiši ekipe, odaberi kup ili svi protiv svih, dobiješ gotov
              raspored po kolima i terenima.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Trofej">🏆</span></div>
            <h3>Mini liga među prijateljima</h3>
            <p>
              Organiziraš mini ligu s istom ekipom kroz sezonu? Round-robin
              raspored svi protiv svih s opcijom dvokružnog sustava
              (domaća/gostujuća utakmica) - kao u pravoj HNL.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Posao">💼</span></div>
            <h3>Firmaški sportski dan</h3>
            <p>
              Team building s više odjela? Odaberi kup sustav, alat raspoređuje
              eliminacije s četvrtfinalom, polufinalom i finalom - bez potrebe
              za ručnim ždrijebom.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Škola">🎒</span></div>
            <h3>Školska i studentska natjecanja</h3>
            <p>
              Razredni turnir, fakultetska liga, sveučilišno prvenstvo -
              generator radi za bilo koji broj ekipa od 2 do 32, s vremenskim
              slotovima po kolu.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Košarka">🏀</span></div>
            <h3>Košarka, odbojka, rukomet</h3>
            <p>
              Raspored utakmica radi identično za sve ekipne sportove. Košarkaški
              3x3 turnir, odbojka na pijesku, rukometni kup - isti alat,
              ista logika.
            </p>
          </article>

          <article className="landing-feature">
            <div className="landing-feature-icon"><span role="img" aria-label="Lopta">🎱</span></div>
            <h3>Pikado, bilijar, stolni tenis</h3>
            <p>
              Kafićko natjecanje u pikadu, bilijar turnir između kvartovskih
              ekipa ili stolnotniski kup - round-robin ili eliminacija, raspored
              spreman u 20 sekundi.
            </p>
          </article>
        </div>
      </section>

      <section className="landing-section landing-section-alt">
        <div className="section-header landing-section-header">
          <h2>Kako radi generator rasporeda turnira</h2>
          <p className="landing-section-sub">
            Bez registracije, bez papirića, bez Excel tablica. Od unosa ekipa
            do gotovog rasporeda - manje od minute.
          </p>
        </div>

        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-num">1</div>
            <h3>Unesi ekipe</h3>
            <p>Upiši ili zalijepi nazive ekipa - svaki u novi red ili odvojen zarezom.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">2</div>
            <h3>Odaberi format</h3>
            <p>Svi protiv svih za mini ligu ili kup sustav za eliminaciju - klikni što ti treba.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">3</div>
            <h3>Postavi detalje</h3>
            <p>Termin početka, trajanje utakmice, broj terena - alat sve automatski izračuna.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">4</div>
            <h3>Podijeli raspored</h3>
            <p>Kopiraj, pošalji u WhatsApp ili isprintaj - raspored je spreman za ekipu.</p>
          </div>
        </div>
      </section>

      <section id="faq" className="landing-section">
        <div className="section-header landing-section-header">
          <h2>Često postavljena pitanja o rasporedu turnira</h2>
          <p className="landing-section-sub">
            Sve što trebaš znati o generiranju rasporeda svi protiv svih,
            kup sustava i eliminacije za tvoj nogometni ili sportski turnir.
          </p>
        </div>

        <div className="faq-list">
          <FaqItem question="Koja je razlika između svi protiv svih i kup sustava?">
            <p>
              Sustav <strong>svi protiv svih</strong> (round-robin) znači da
              svaka ekipa odigra utakmicu sa svakom drugom - kao u HNL-u ili
              Ligi prvaka u grupnoj fazi. Idealno kad imaš manji broj ekipa
              (3-12) i dovoljno vremena. <strong>Kup sustav</strong>
              (eliminacija) znači da tko izgubi - ispada. Brže se odvija,
              dobar za veći broj ekipa i vikend turnire, ali ekipe igraju
              manje utakmica.
            </p>
          </FaqItem>

          <FaqItem question="Koliko ekipa mogu unijeti u generator rasporeda?">
            <p>
              Alat podržava od 2 do 32 ekipe, što pokriva praktički svaki
              rekreativni nogometni turnir, firmaški kup ili mini ligu.
              Kod kup sustava, ako broj ekipa nije potencija broja 2
              (4, 8, 16, 32), najboljim seedovima se dodjeljuje slobodan
              prolaz (bye) u sljedeće kolo - standardno pravilo u turnirskom
              formatu.
            </p>
          </FaqItem>

          <FaqItem question="Što znači dvokružni sustav kod svi protiv svih?">
            <p>
              U jednokružnom sustavu svaka ekipa igra sa svakom jedanput
              (npr. 6 ekipa = 15 utakmica). U dvokružnom svaka ekipa igra sa
              svakom dvaput - jednom kao domaćin, jednom kao gost
              (npr. 6 ekipa = 30 utakmica). Koristi se u HNL-u i većini
              pravih liga. Uključi ovu opciju ako organiziraš sezonu koja
              traje više tjedana.
            </p>
          </FaqItem>

          <FaqItem question="Može li raspored pokriti više terena istovremeno?">
            <p>
              Da. U naprednim postavkama upišeš broj terena (do 8) i alat
              raspoređuje utakmice iz istog kola paralelno. Ako imaš 4 ekipe
              i 2 terena, obje utakmice prvog kola igraju se istovremeno,
              što drastično skraćuje trajanje turnira. Posebno korisno za
              vikend turnire ili sportski dan u firmi.
            </p>
          </FaqItem>

          <FaqItem question="Alat izračunava vremena utakmica?">
            <p>
              Da. Upišeš vrijeme početka turnira, trajanje utakmice i pauzu
              između kola, a generator ispiše točno vrijeme za svaku utakmicu.
              Ako imaš više terena, to se također uzima u obzir - utakmice
              koje idu paralelno imaju isto vrijeme početka. Savršeno za
              printanje rasporeda i objavu na grupi.
            </p>
          </FaqItem>

          <FaqItem question="Mogu li isprintati raspored turnira?">
            <p>
              Naravno. Klikni "Isprintaj" i dobiješ čistu verziju za papir,
              bez navigacije i ostalih elemenata stranice - samo raspored
              po kolima s vremenima i ekipama. Idealno za zalijepiti na
              oglasnu ploču kluba, dvorane ili terena.
            </p>
          </FaqItem>

          <FaqItem question="Radi li generator rasporeda za druge sportove?">
            <p>
              Da, iako smo ga dizajnirali za rekreativni nogomet, logika
              rasporeda je ista za sve ekipne sportove - košarku, futsal,
              odbojku, rukomet, hokej, pikado, bilijar, stolni tenis. Bilo
              koji turnir ili liga u kojoj se ekipe uparuju po utakmicama,
              može koristiti ovaj alat.
            </p>
          </FaqItem>

          <FaqItem question="Pamti li alat moj raspored i rezultate?">
            <p>
              Ne, ovaj generator rasporeda je potpuno anoniman - sve radi u
              tvom pregledniku i ništa se ne sprema. Ako organiziraš
              rekreativni nogomet s istom ekipom i želiš pratiti rezultate,
              statistiku igrača, MVP rang listu i arhivu cijele sezone,
              preporučamo da otvoriš besplatan StatArena račun - aplikacija
              je dizajnirana upravo za trajno praćenje rekreativnih klubova
              i ekipa.
            </p>
          </FaqItem>

          <FaqItem question="Je li algoritam rasporeda pravedan?">
            <p>
              Da. Za svi protiv svih koristimo klasičan circle (Berger) metod
              koji garantira da svaka ekipa odigra svaku utakmicu točno
              onoliko puta koliko si odabrao, i da je broj domaćih/gostujućih
              utakmica balansiran. Za kup sustav koristimo nasumični ždrijeb
              s automatskim rasporedom slobodnih prolaza (bye) prema
              standardnom turnirskom pravilu - najviši seedovi ne idu jedni
              protiv drugih u prvoj rundi.
            </p>
          </FaqItem>
        </div>
      </section>

      <section className="landing-cta">
        <h2>Organiziraš rekreativni nogomet kroz cijelu sezonu?</h2>
        <p>
          Ako tvoja ekipa igra svaki tjedan, StatArena ti može napraviti
          puno više od rasporeda turnira - balansirane timove po statistici
          igrača, MVP rang listu, arhivu sezone i sve rezultate na jednom
          mjestu. Sve besplatno.
        </p>
        <div className="landing-cta-actions">
          <Link to="/register" className="btn btn-primary btn-lg">
            Registriraj svoju ekipu besplatno
          </Link>
          <Link to="/generator-timova" className="btn btn-secondary btn-lg">
            Probaj generator timova
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <span className="landing-logo-mark" role="img" aria-label="Nogometna lopta">⚽</span>
            <span className="landing-logo-text">StatArena</span>
            <span className="landing-footer-tag">Aplikacija za rekreativni nogomet</span>
          </div>
          <div className="landing-footer-links">
            <Link to="/">Početna</Link>
            <Link to="/generator-timova">Generator timova</Link>
            <Link to="/raspored-turnira">Raspored turnira</Link>
            <Link to="/semafor-online">Online semafor</Link>
            <Link to="/login">Prijava</Link>
            <Link to="/register">Registracija</Link>
          </div>
          <div className="landing-footer-copy">
            © {new Date().getFullYear()} StatArena · statarena.app
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RasporedTurnira;

